import * as React from "react"
import { tileGridStats } from "../modelling/tile_grid"
import { DatasetLayer, Layer, ModelOutputLayer } from "../state"

export type TileGridType = NonNullable<tileGridStats["type"]>

export type ModelDatasetLayer = ModelOutputLayer | DatasetLayer

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export function isModelDatasetLayer(layer: Layer): layer is ModelDatasetLayer {
  return layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer"
}

export function isTileGridLoaded(stats: tileGridStats): stats is tileGridStats & { type: TileGridType } {
  return stats.type !== undefined
}

/** Visual style fields copied between model/dataset layers with the same grid type. */
export function extractModelDatasetLayerStyle(layer: ModelDatasetLayer): Partial<ModelDatasetLayer> {
  return {
    opacity: layer.opacity,
    fill: layer.fill,
    colors: layer.colors ? cloneJson(layer.colors) : undefined,
    colorByLabel: layer.colorByLabel ? cloneJson(layer.colorByLabel) : undefined,
    overrideBounds: layer.overrideBounds,
    bounds: layer.bounds ? ([layer.bounds[0], layer.bounds[1]] as [number, number]) : undefined,
  }
}

export type ModelDatasetStyleCopyEntry = {
  id: number | string
  name: string
  layer: ModelDatasetLayer
  gridType: TileGridType
}

export function buildModelDatasetStyleCopyEntries(
  layers: Record<number, Layer>,
  getLayerData: (layer: ModelDatasetLayer) => tileGridStats
): ModelDatasetStyleCopyEntry[] {
  return Object.entries(layers).flatMap(([id, layer]) => {
    if (!isModelDatasetLayer(layer)) {
      return []
    }
    const stats = getLayerData(layer)
    if (!isTileGridLoaded(stats)) {
      return []
    }
    return [{ id: Number(id), name: layer.name, layer, gridType: stats.type }]
  })
}

export function buildModelDatasetStyleCopyEntriesFromList(
  entries: Array<{ id: string; layer: Layer }>,
  statsById: Record<string, tileGridStats>
): ModelDatasetStyleCopyEntry[] {
  return entries.flatMap(({ id, layer }) => {
    if (!isModelDatasetLayer(layer)) {
      return []
    }
    const stats = statsById[id]
    if (stats === undefined || !isTileGridLoaded(stats)) {
      return []
    }
    return [{ id, name: layer.name, layer, gridType: stats.type }]
  })
}

export function listModelDatasetStyleCopySources(
  entries: ModelDatasetStyleCopyEntry[],
  targetId: number | string,
  targetGridType: TileGridType
): ModelDatasetStyleCopyEntry[] {
  return entries.filter(entry => entry.id !== targetId && entry.gridType === targetGridType)
}

export type CopyLayerStyleControlProps = {
  targetLayerId: number | string
  targetGridType: TileGridType
  entries: ModelDatasetStyleCopyEntry[]
  onApply: (style: Partial<ModelDatasetLayer>, source: ModelDatasetStyleCopyEntry) => void
}

export const CopyLayerStyleControl: React.FC<CopyLayerStyleControlProps> = ({
  targetLayerId,
  targetGridType,
  entries,
  onApply,
}) => {
  const compatible = React.useMemo(
    () => listModelDatasetStyleCopySources(entries, targetLayerId, targetGridType),
    [entries, targetLayerId, targetGridType]
  )
  const [sourceId, setSourceId] = React.useState<string>("")

  React.useEffect(() => {
    setSourceId("")
  }, [targetLayerId, targetGridType])

  if (compatible.length === 0) {
    return null
  }

  const handleApply = () => {
    const source = compatible.find(entry => String(entry.id) === sourceId)
    if (source === undefined) {
      return
    }
    onApply(extractModelDatasetLayerStyle(source.layer), source)
  }

  return (
    <div className="mt-3">
      <div className="small font-weight-bold text-muted mb-1">Copy style from</div>
      <div className="d-flex align-items-center">
        <select
          className="custom-select custom-select-sm flex-grow-1"
          value={sourceId}
          onChange={event => setSourceId(event.target.value)}
          aria-label="Source layer for style copy"
        >
          <option value="">Choose layer…</option>
          {compatible.map(entry => (
            <option key={String(entry.id)} value={String(entry.id)}>
              {entry.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary ml-2 text-nowrap"
          disabled={sourceId === ""}
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  )
}
